import React, { useState, useEffect, useRef } from "react";
import Icon from "../../components/ui/icon";
import "../../assets/styles/pages/publishers/invoiceDetails.css";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import DOMPurify from "dompurify";
import AdminSideNav from "../../components/layout/adminSideNav";
import moment from "moment-timezone";
import NotAuthorizedMessage from "../../components/feedback/notAuthorizedMessage";
import { getTimezoneByCountry } from "../../utils/getTimezoneByCountry";
import { storageManager } from "../../utils/storageManager";
import { convertCurrency } from "../../utils/currencyConverter";

const AdminInvoicesPage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("AdminInfo")) || {}
  );
  const country = userInfo?.country?.toLowerCase() || "india";
  const [userVerified, setUserVerified] = useState(false);
  const [isApproving, setIsApproving] = useState(null);
  const [isRejecting, setIsRejecting] = useState(null);
  const [isPaying, setIsPaying] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, sethasMore] = useState(true);
  const limit = 20;
  const [fetchStatus, setfetchStatus] = useState("fetching_first");
  const [searchInvoiceId, setSearchInvoiceId] = useState("");
  const [isSearchingInvoice, setisSearchingInvoice] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const isLoadingMore = useRef(false);
  const isNotAdminUser = userInfo.role && userInfo.role !== "admin";
  const currency = storageManager.getCurrency();

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    const validateUser = async () => {
      if (userVerified) return;

      api
        .get(`/api/admin/info`)
        .then((response) => {
          setUserInfo(response.data.data);
          setUserVerified(true);
          localStorage.setItem("AdminInfo", JSON.stringify(response.data.data));
          if (response.data.data.role !== "admin") return;
          fetchInvoices();
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err.response?.data?.message || "Login not found.",
          });
          navigate("/auth/login");
        });
    };

    const fetchInvoices = async () => {
      if (isNotAdminUser) return;
      if (fetchStatus === "fetching") return;
      setfetchStatus("fetching");

      api
        .get(
          `/api/a/invoice/?limit=${limit}&skip=${skip}&status=${statusFilter}`
        )
        .then((response) => {
          setInvoices((prev) => [...prev, ...response.data.data]);
          setSkip((prev) => prev + limit);
          setfetchStatus("fetched");

          isLoadingMore.current = false;

          if (response.data.data.length < limit) {
            sethasMore(false);
            window.removeEventListener("scroll", handelScroll);
          }
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err?.response?.data?.message || "Error Fetching Invoices",
          });
        });
    };

    const handelScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight && hasMore) {
        if (fetchStatus === "fetching") return;
        if (fetchStatus === "fetching_first") return;
        if (isLoadingMore.current) return;
        isLoadingMore.current = true;
        fetchInvoices();
      }
    };

    validateUser();

    window.addEventListener("scroll", handelScroll);
    return () => window.removeEventListener("scroll", handelScroll);
  }, [navigate, showToast, userVerified, fetchStatus, skip, statusFilter, hasMore, isNotAdminUser]);

  const handelSearchInvoiceIdChange = (e) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setSearchInvoiceId(sanitizedValue);

    if (sanitizedValue.length === 0) {
      if (fetchStatus === "fetching_first") return;
      setfetchStatus("fetching_first");
      setInvoices([]);
      setSkip(0);
      api
        .get(`/api/a/invoice/?limit=${limit}&skip=0&status=${statusFilter}`)
        .then((response) => {
          setInvoices([...response.data.data]);
          setSkip((prev) => prev + limit);
          setfetchStatus("fetched");
        })
        .catch((err) => {
          showToast({
            status: "error",
            message: err?.response?.data?.message || "Error Fetching Invoices",
          });
        });
    }
  };

  const searchInvoice = () => {
    if (isSearchingInvoice) return;
    setisSearchingInvoice(true);

    const sanitizedInvoiceId = DOMPurify.sanitize(searchInvoiceId);
    if (sanitizedInvoiceId.length !== 7) {
      showToast({
        status: "error",
        message: "Invalid Invoice ID.",
      });
      setisSearchingInvoice(false);
      return;
    }
    api
      .get(`/api/a/invoice/${sanitizedInvoiceId}`)
      .then((response) => {
        setInvoices([response.data.data]);
        setisSearchingInvoice(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Invoice not found.",
        });
        setisSearchingInvoice(false);
      });
  };

  const handleApprove = (invoiceId) => {
    if (isApproving) return;
    setIsApproving(invoiceId);
    api
      .post(`/api/a/invoice/${invoiceId}/approve`)
      .then((response) => {
        showToast({
          status: "success",
          message: response.data.message,
        });
        setInvoices((prev) => {
          return prev.map((invoice) => {
            if (invoice.invoiceId === invoiceId) {
              return { ...invoice, status: "approved" };
            }
            return invoice;
          });
        });
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
      })
      .finally(() => {
        setIsApproving(null);
      });
  };

  const handlePaid = (invoiceId) => {
    if (isPaying) return;
    setIsPaying(invoiceId);
    api
      .post(`/api/a/invoice/${invoiceId}/paid`)
      .then((response) => {
        showToast({
          status: "success",
          message: response.data.message,
        });
        setInvoices((prev) => {
          return prev.map((invoice) => {
            if (invoice.invoiceId === invoiceId) {
              return { ...invoice, status: "paid" };
            }
            return invoice;
          });
        });
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
      })
      .finally(() => {
        setIsPaying(null);
      });
  };

  const handleReject = (invoiceId) => {
    if (isRejecting) return;
    setIsRejecting(invoiceId);
    api
      .post(`/api/a/invoice/${invoiceId}/reject`)
      .then((response) => {
        showToast({
          status: "success",
          message: response.data.message,
        });
        setInvoices((prev) => {
          return prev.map((invoice) => {
            if (invoice.invoiceId === invoiceId) {
              return { ...invoice, status: "rejected" };
            }
            return invoice;
          });
        });
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
      })
      .finally(() => {
        setIsRejecting(null);
      });
  };

  const handelStatusFilterChange = (status) => {
    if (statusFilter === status) return;
    setfetchStatus("fetching_first");
    setInvoices([]);
    setSkip(0);
    setStatusFilter(status);
    api
      .get(`/api/a/invoice/?limit=${limit}&skip=0&status=${status}`)
      .then((response) => {
        setInvoices([...response.data.data]);
        setSkip((prev) => prev + limit);
        setfetchStatus("fetched");
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err?.response?.data?.message || "Error Fetching Invoices",
        });
      });
  };

  return (
    <div className="ainvoice__page">
      <AdminSideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>Invoices</h2>
            <p>Manage invoices requested by publishers from here.</p>
          </div>
        </header>

        <header className="mobile-header">
          <div>
            <h2>Invocies</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>

        {userInfo && userInfo.role !== "admin" && (
          <NotAuthorizedMessage />
        )}

        <div className="invoice-filters">
          <div className="status-filters">
            <div
              className={`status-filter ${statusFilter === "all" ? "active" : ""
                }`}
              onClick={() => handelStatusFilterChange("all")}
            >
              All
            </div>
            <div
              className={`status-filter pending ${statusFilter === "pending" ? "active" : ""
                }`}
              onClick={() => handelStatusFilterChange("pending")}
            >
              Pending
            </div>
            <div
              className={`status-filter paid ${statusFilter === "approved" ? "active" : ""
                }`}
              onClick={() => handelStatusFilterChange("approved")}
            >
              Approved
            </div>
            <div
              className={`status-filter paid ${statusFilter === "paid" ? "active" : ""
                }`}
              onClick={() => handelStatusFilterChange("paid")}
            >
              Paid
            </div>
            <div
              className={`status-filter rejected ${statusFilter === "rejected" ? "active" : ""
                }`}
              onClick={() => handelStatusFilterChange("rejected")}
            >
              Rejected
            </div>
          </div>
          <div className="search-invoice">
            <input
              type="number"
              placeholder="Search Invoice ID"
              value={searchInvoiceId}
              onChange={handelSearchInvoiceIdChange}
            />
            <button onClick={searchInvoice}>
              {isSearchingInvoice ? (
                <Icon name="ArrowPath" className="spin" />
              ) : (
                <Icon name="MagnifyingGlass" />
              )}
            </button>
          </div>
        </div>

        <div className="invoice-container">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>Publisher</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Payout Method</th>
                <th>Requested Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fetchStatus === "fetching" ||
                fetchStatus === "fetching_first" ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index} className="loading">
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                    <td className="loading">
                      <div className="loading">Loading...</div>
                    </td>
                  </tr>
                ))
              ) : invoices && invoices.length > 0 ? (
                invoices.map((invoice, index) => (
                  <tr
                    key={invoice.invoiceId}
                    className={index % 2 === 0 ? "even-row" : ""}
                  >
                    <td>{invoice.invoiceId}</td>
                    <td>{invoice.publisherName}</td>
                    <td>{convertCurrency(invoice.amount, "INR", currency)}</td>
                    <td>
                      <span
                        className={`status ${invoice.status === "pending"
                          ? "pending"
                          : invoice.status === "paid" ||
                            invoice.status === "approved"
                            ? "paid"
                            : "rejected"
                          }`}
                      >
                        {invoice.status.charAt(0).toUpperCase() +
                          invoice.status.slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td>{invoice.payoutType.toUpperCase()}</td>
                    <td>
                      {moment
                        .tz(invoice.withdrawalRequestedAt, getTimezoneByCountry(country))
                        .format("DD MMM YYYY, h:mm A")}
                    </td>
                    <td className="action-buttons">
                      {invoice.status === "pending" ||
                        invoice.status === "rejected" ? (
                        <button
                          className="approve-btn"
                          onClick={() => handleApprove(invoice.invoiceId)}
                          disabled={isApproving === invoice.invoiceId}
                        >
                          {isApproving === invoice.invoiceId ? (
                            <Icon name="ArrowPath" className="spin" />
                          ) : (
                            <Icon name="CheckCircle" />
                          )}
                          {isApproving === invoice.invoiceId
                            ? "Approving..."
                            : "Approve"}
                        </button>
                      ) : invoice.status === "approved" ? (
                        <button
                          className="approve-btn"
                          onClick={() => handlePaid(invoice.invoiceId)}
                          disabled={isPaying === invoice.invoiceId}
                        >
                          {isPaying === invoice.invoiceId ? (
                            <Icon name="ArrowPath" className="spin" />
                          ) : (
                            <Icon name="CheckCircle" />
                          )}
                          {isPaying === invoice.invoiceId
                            ? "Updating..."
                            : "Paid"}
                        </button>
                      ) : null}
                      {invoice.status !== "rejected" && (
                        <button
                          className="reject-btn"
                          onClick={() => handleReject(invoice.invoiceId)}
                          disabled={isRejecting === invoice.invoiceId}
                        >
                          {isRejecting === invoice.invoiceId ? (
                            <Icon name="ArrowPath" className="spin" />
                          ) : (
                            <Icon name="XMark" />
                          )}
                          {isRejecting === invoice.invoiceId
                            ? "Rejecting..."
                            : "Reject"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="no-data">
                    No invoice found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <span className="copyright-text">
          © 2025 Swift Digital Ads, All rights reserved
        </span>
      </main>
    </div>
  );
};

export default AdminInvoicesPage;
